import type { IconDefinition } from "@fortawesome/fontawesome-svg-core";
import { styled, SxProps, Theme } from "@mui/material/styles";
import SvgIcon, { SvgIconProps } from "@mui/material/SvgIcon";
import { forwardRef } from "react";

export type FaIconProps = {
  icon: IconDefinition;
  /**
   * Uses the icon's natural aspect ratio. Omit to allow a contextual default, which is natural
   * width generally and fixed width inside ListItemIcon. Set explicitly to override the context.
   */
  automaticWidth?: boolean;
  /**
   * Determines opacity to use for the primary path in duotone icons (should be a number between 0 and 1)
   * @default 1.0
   */
  faPrimaryOpacity?: number;
  /**
   * Determines opacity to use for the secondary path in duotone icons (should be a number between 0 and 1)
   * @default 0.4
   */
  faSecondaryOpacity?: number;
} & Omit<SvgIconProps, "viewBox" | "css">;

const Path = styled("path")<{ opacity: number }>(({ opacity }) => ({
  opacity,
}));

// Font Awesome 7 uses this shared outer width by default while preserving each icon's viewBox.
// @see https://docs.fontawesome.com/upgrade/whats-changed#fixed-width-icons-by-default
export const FA_ICON_DEFAULT_WIDTH_CSS_VARIABLE = "--FaIcon-default-width";
export const FA_ICON_DEFAULT_FONT_SIZE_CSS_VARIABLE = "--FaIcon-default-font-size";
export const FONT_AWESOME_FIXED_WIDTH = "1.25em";

const resolveWidth = (automaticWidth: boolean | undefined, naturalWidth: string): string => {
  if (automaticWidth == null) {
    return `var(${FA_ICON_DEFAULT_WIDTH_CSS_VARIABLE}, ${naturalWidth})`;
  }
  return automaticWidth ? naturalWidth : FONT_AWESOME_FIXED_WIDTH;
};

const normalizeSx = (sx?: SxProps<Theme>) => {
  if (!sx) return [];
  return Array.isArray(sx) ? sx : [sx];
};

/**
 * MUI Wrapper for FontAwesome Icons (see https://mui.com/components/icons/#font-awesome) to allow for correct sizing (and to unlock the `sx` prop)
 */
export const FaIcon = forwardRef<SVGSVGElement, FaIconProps>(
  (
    {
      icon,
      automaticWidth,
      faPrimaryOpacity = 1,
      faSecondaryOpacity = 0.4,
      fontSize,
      sx,
      ...props
    },
    ref,
  ) => {
    const {
      icon: [width, height, , , svgPathData],
    } = icon;
    const naturalWidth = `${width / height}em`;
    const computedWidth = resolveWidth(automaticWidth, naturalWidth);
    // Some v7 artwork intentionally extends above or below the base canvas.
    // Match Font Awesome's inline-SVG overflow so MUI SvgIcon does not crop it.
    // Keep sizing on SvgIcon so explicit variants and inherited container sizes
    // scale the selected canvas aspect ratio proportionally.
    // @see https://docs.fontawesome.com/web/style/icon-canvas
    const normalizedSx: SxProps<Theme> = [
      { overflow: "visible", width: computedWidth },
      ...(fontSize == null
        ? [
            (theme: Theme) => ({
              fontSize: `var(${FA_ICON_DEFAULT_FONT_SIZE_CSS_VARIABLE}, ${theme.typography.svgIcon?.fontSize ?? "1rem"})`,
            }),
          ]
        : []),
      ...normalizeSx(sx),
    ];

    return (
      <SvgIcon
        ref={ref}
        fontSize={fontSize ?? "inherit"}
        {...props}
        sx={normalizedSx}
        viewBox={`0 0 ${width} ${height}`}
      >
        {typeof svgPathData === "string" ? (
          <path d={svgPathData} />
        ) : (
          /**
           * A multi-path Font Awesome icon seems to imply a duotune icon. The 0th path seems to
           * be the faded element (referred to as the "secondary" path in the Font Awesome docs)
           * of a duotone icon. 40% is the default opacity.
           *
           * @see https://fontawesome.com/how-to-use/on-the-web/styling/duotone-icons#changing-opacity
           */
          svgPathData.map((d: string, i: number) => (
            <Path
              key={d}
              className={`MuiFaIcon-${i === 0 ? "Secondary" : "Primary"}Path`}
              opacity={i === 0 ? faSecondaryOpacity : faPrimaryOpacity}
              d={d}
            />
          ))
        )}
      </SvgIcon>
    );
  },
);
